import L from 'leaflet'
import { useContext, useEffect, useRef } from 'react'
import { PublicTransportStationFeature } from '@digimap/types'
import getPublicTransportLineColor from '../../lib/getPublicTransportLineColor'
import { LeafletContext } from '../leaflet-react/contexts/LeafletContext'

interface PublicTransportMarkersProps {
  startStations?: PublicTransportStationFeature[]
  endStations?: PublicTransportStationFeature[]
  visible: boolean
}

export default function PublicTransportMarkers({
  startStations = [],
  endStations = [],
  visible,
}: PublicTransportMarkersProps) {
  const { state } = useContext(LeafletContext)
  const markersRef = useRef<L.Marker[]>([])

  useEffect(() => {
    if (!state.map) return

    const map = state.map

    // Clear existing markers
    markersRef.current.forEach((marker) => {
      marker.remove()
    })
    markersRef.current = []

    if (!visible) return

    // Combine all stations with their type (start/end) and index
    const allStations: Array<{
      station: PublicTransportStationFeature
      isStart: boolean
      index: number
    }> = [
      ...startStations.map((station, index) => ({
        station,
        isStart: true,
        index,
      })),
      ...endStations.map((station, index) => ({
        station,
        isStart: false,
        index,
      })),
    ]

    allStations.forEach(({ station, isStart, index }) => {
      const isMain = index === 0
      const scale = isMain ? 0.7 : 0.4
      const fillColor = getPublicTransportLineColor(station.properties.line)
      const [lng, lat] = station.geometry.coordinates

      const baseSize = 35
      const baseHeight = 48
      const scaledSize = baseSize * scale
      const scaledHeight = baseHeight * scale

      const svgIcon = `
        <svg width="${scaledSize}" height="${scaledHeight}" viewBox="-17.7 -48 35.4 48" xmlns="http://www.w3.org/2000/svg">
          <path d="M0-48c-9.8 0-17.7 7.8-17.7 17.4 0 15.5 17.7 30.6 17.7 30.6s17.7-15.4 17.7-30.6c0-9.6-7.9-17.4-17.7-17.4z" 
                fill="${fillColor}" 
                fill-opacity="0.8" 
                stroke="#000" 
                stroke-width="1"/>
        </svg>
      `

      const icon = L.divIcon({
        html: svgIcon,
        className: 'public-transport-marker',
        iconSize: [scaledSize, scaledHeight],
        iconAnchor: [scaledSize / 2, scaledHeight],
      })

      const marker = L.marker([lat, lng], { icon }).addTo(map)

      // Bind popup with station name and line
      const popupContent = `<div style="width:150px; text-align: center;">${station.properties.name} / ${station.properties.line}</div>`
      marker.bindPopup(popupContent, {
        offset: [0, -30],
      })

      markersRef.current.push(marker)
    })

    return () => {
      markersRef.current.forEach((marker) => {
        marker.remove()
      })
      markersRef.current = []
    }
  }, [state.map, startStations, endStations, visible])

  return null
}
